import React, { useEffect, useState } from "react";
import { docFileName } from "@/lib/utils";
import api, { API, formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Send, X, FileCode2, Truck, SearchCheck } from "lucide-react";
import DocRowActions from "@/components/DocRowActions";
import { hasSignedXml } from "@/lib/docActions";
import PrintMenu from "@/components/PrintMenu";
import { formatDOP, formatDate } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { DocBuilder } from "@/pages/Documents";
import DeliveryNoteBuilder from "@/components/DeliveryNoteBuilder";
import DocSearchBar from "@/components/DocSearchBar";
import { useConfirm } from "@/components/ConfirmDialog";

const statusColor = {
  emitida: "bg-success/10 text-success border-success/30",
  anulada: "bg-destructive/10 text-destructive border-destructive/30",
};
const ecfStatusLabel = {
  borrador: "Borrador",
  enviado: "Enviado",
  aceptado: "Aceptada DGII",
  rechazado: "Rechazada DGII",
};

async function downloadFrom(url, fallbackName) {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) {
    const txt = await r.text();
    throw new Error(txt || `Error ${r.status}`);
  }
  const cd = r.headers.get("content-disposition") || "";
  const m = cd.match(/filename="([^"]+)"/);
  const name = m ? m[1] : fallbackName;
  const blob = await r.blob();
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(u);
}

export default function Sales() {
  const [invoices, setInvoices] = useState([]);
  const [invoicesWithConduce, setInvoicesWithConduce] = useState(new Set());
  const confirm = useConfirm();
  // Búsqueda multi-criterio
  const [search, setSearch] = useState("");
  const [criteria, setCriteria] = useState({ number: true, customer: true, ncf: true });
  // Conduce desde factura
  const [conduceFromInv, setConduceFromInv] = useState(null);

  const load = async () => {
    const [invRes, dnRes] = await Promise.all([
      api.get("/invoices"),
      api.get("/delivery-notes"),
    ]);
    setInvoices(invRes.data);
    setInvoicesWithConduce(new Set(
      (dnRes.data || []).map((d) => d.invoice_id).filter(Boolean),
    ));
  };

  useEffect(() => {
    load();
  }, []);

  const filteredInvoices = React.useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return invoices;
    return invoices.filter((inv) => {
      if (criteria.number && (inv.number || "").toLowerCase().includes(s)) return true;
      if (criteria.customer && (inv.customer_name || "").toLowerCase().includes(s)) return true;
      if (criteria.ncf && (inv.ncf || "").toLowerCase().includes(s)) return true;
      return false;
    });
  }, [invoices, search, criteria]);

  const createConduce = async (payload) => {
    try {
      await api.post("/delivery-notes", payload);
      toast.success("Conduce creado");
      setConduceFromInv(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      throw err;
    }
  };

  const sendEcf = async (id) => {
    try {
      const { data } = await api.post(`/invoices/${id}/send-ecf`);
      toast.success(`Enviada a DGII (track: ${data.track_id})`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const verifyEcf = async (ncf) => {
    try {
      const { data } = await api.post(`/dgii/phase4/refresh-status/${ncf}`);
      const estado = data.new_status || "desconocido";
      if (estado === "aceptado") {
        toast.success(`DGII: Aceptado · ${ncf}`);
      } else if (estado === "rechazado") {
        toast.error(`DGII: Rechazado · ${data.error || ncf}`);
      } else {
        toast.message(`DGII: ${estado} · ${ncf}`);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cancel = async (inv) => {
    const ok = await confirm({
      title: `Anular factura ${inv.number}`,
      description: "El stock se devolverá y la factura quedará marcada como anulada.",
      confirmLabel: "Anular",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Factura anulada");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadPdf = async (inv) => {
    try {
      await downloadFrom(`${API}/invoices/${inv.id}/pdf`, `${inv.number}.pdf`);
    } catch (e) { toast.error(e.message || "Error generando PDF"); }
  };

  const downloadXml = async (inv) => {
    try {
      await downloadFrom(`${API}/invoices/${inv.id}/ecf-xml`, `${inv.ncf || inv.number}.xml`);
    } catch (e) { toast.error(e.message || "Error descargando XML"); }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Facturas"
        subtitle="Historial completo de ventas. Envío e-CF, anulación y reimpresión."
        testid="sales-header"
      />

      <DocSearchBar
        value={search}
        onChange={setSearch}
        criteria={criteria}
        onCriteriaChange={setCriteria}
        options={[
          { key: "number", label: "Número" },
          { key: "customer", label: "Cliente" },
          { key: "ncf", label: "NCF" },
        ]}
        placeholder="Buscar factura por número, cliente o NCF…"
        testidPrefix="sales-search"
        totalLabel={`${filteredInvoices.length} de ${invoices.length}`}
      />

      <div className="bg-card border border-border rounded-md overflow-x-auto" data-testid="invoices-table">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-3">Número</th>
              <th className="text-left p-3">NCF</th>
              <th className="text-left p-3">Cliente</th>
              <th className="text-left p-3">Fecha</th>
              <th className="text-right p-3">Total</th>
              <th className="text-right p-3">Saldo</th>
              <th className="text-center p-3">Pago</th>
              <th className="text-center p-3">Estado</th>
              <th className="text-center p-3">e-CF</th>
              <th className="text-right p-3 w-52">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map((inv) => (
              <tr key={inv.id} className="border-b border-border/60 hover:bg-accent">
                <td className="p-3 data-cell font-bold">{inv.number}</td>
                <td className="p-3 data-cell text-xs">{inv.ncf}</td>
                <td className="p-3">
                  <div className="font-semibold">{inv.customer_name}</div>
                  {inv.customer_rnc_cedula && <div className="text-xs text-muted-foreground data-cell">{inv.customer_rnc_cedula}</div>}
                </td>
                <td className="p-3 text-xs text-muted-foreground">{formatDate(inv.created_at)}</td>
                <td className="p-3 text-right data-cell font-bold">{formatDOP(inv.total)}</td>
                <td className="p-3 text-right data-cell text-primary font-bold">{formatDOP(inv.balance_due || 0)}</td>
                <td className="p-3 text-center text-xs">
                  <div className="flex flex-col items-center gap-0.5">
                    <span className={`px-2 py-0.5 rounded ${
                      inv.payment_method === "credito"
                        ? "bg-orange-500/15 text-orange-700 dark:text-orange-400 font-bold"
                        : "bg-muted text-muted-foreground"
                    }`}>
                      {inv.payment_method === "credito" ? "CRÉDITO" : (inv.payment_method || "—").toUpperCase()}
                    </span>
                    {inv.payment_method === "credito" && inv.due_date && (
                      <span
                        className={`text-[10px] data-cell ${
                          new Date(inv.due_date) < new Date() && (inv.balance_due || 0) > 0
                            ? "text-destructive font-bold"
                            : "text-muted-foreground"
                        }`}
                        title={`Vence: ${formatDate(inv.due_date)}`}
                        data-testid={`due-${inv.number}`}
                      >
                        vence {formatDate(inv.due_date)}
                      </span>
                    )}
                    <span className={`text-[10px] px-1.5 rounded ${
                      inv.payment_status === "pagada" ? "text-success" :
                      inv.payment_status === "parcial" ? "text-amber-600" :
                      "text-destructive"
                    }`}>
                      {inv.payment_status || "—"}
                    </span>
                  </div>
                </td>
                <td className="p-3 text-center">
                  <Badge className={`border ${statusColor[inv.status] || ""}`} variant="outline">
                    {inv.status}
                  </Badge>
                  {inv.has_credit_note && (
                    <div
                      className="mt-1 text-[10px] px-2 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 font-semibold inline-block"
                      title={`Nota de crédito emitida: ${inv.credit_note_number || ""}`}
                      data-testid={`nc-badge-${inv.number}`}
                    >
                      NC {inv.credit_note_number || ""}
                    </div>
                  )}
                </td>
                <td className="p-3 text-center text-xs">
                  {inv.is_electronic ? (
                    <span className={`px-2 py-0.5 rounded ${inv.ecf_status === "aceptado" ? "bg-success/10 text-success" : "bg-amber-500/10 text-amber-600"}`}>
                      {ecfStatusLabel[inv.ecf_status] || "—"}
                    </span>
                  ) : <span className="text-muted-foreground">N/A</span>}
                </td>
                <td className="p-3 text-right">
                  <DocRowActions
                    printUrl={`/invoices/${inv.id}/pdf`}
                    filename={docFileName(inv)}
                    onXml={hasSignedXml(inv) ? () => downloadXml(inv) : null}
                    onSend={inv.is_electronic && inv.status !== "anulada" ? () => sendEcf(inv.id) : null}
                    sendTitle={inv.ecf_status === "aceptado" ? "Reenviar a DGII" : "Enviar a DGII"}
                    onVerify={inv.is_electronic && inv.ecf_track_id ? () => verifyEcf(inv.ncf) : null}
                    testids={{ print: `pdf-${inv.number}`, xml: `xml-${inv.number}`, send: `send-ecf-${inv.number}`, verify: `verify-ecf-${inv.number}` }}
                  >
                  {inv.status !== "anulada" && !invoicesWithConduce.has(inv.id) && (
                    <Button
                      size="icon" variant="ghost"
                      onClick={() => setConduceFromInv(inv)}
                      title="Generar conduce"
                      data-testid={`conduce-from-${inv.number}`}
                    >
                      <Truck className="w-4 h-4 text-info" />
                    </Button>
                  )}
                  {invoicesWithConduce.has(inv.id) && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] text-muted-foreground px-1.5"
                      title="Esta factura ya tiene un conduce"
                      data-testid={`conduce-done-${inv.number}`}
                    >
                      <Truck className="w-3 h-3 text-muted-foreground/60" />
                    </span>
                  )}
                  {inv.status !== "anulada" && (
                    <Button size="icon" variant="ghost" onClick={() => cancel(inv)} title="Anular" data-testid={`cancel-${inv.number}`}>
                      <X className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                  </DocRowActions>
                </td>
              </tr>
            ))}
            {filteredInvoices.length === 0 && invoices.length > 0 && <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">Sin facturas que coincidan con la búsqueda.</td></tr>}
            {invoices.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">Sin facturas todavía. Genera una desde POS.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Generar conduce desde factura */}
      <DeliveryNoteBuilder
        open={!!conduceFromInv}
        onOpenChange={(v) => !v && setConduceFromInv(null)}
        onSubmit={createConduce}
        initialInvoiceId={conduceFromInv?.id || ""}
      />
    </PageContainer>
  );
}
